import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { 
  Shield, Lock, Eye, CheckCircle, ArrowRight,
  EyeOff, Clock, MessageSquare, UserX, AlertTriangle
} from 'lucide-react';
import { trackCTAClick } from '@/utils/analytics';

// AEO Schema
const seoSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Complete Guide to Private Messaging - How to Communicate Securely",
  "description": "Learn how to send private messages securely. This guide covers anonymous messaging, disappearing messages, and how to protect your identity online.",
  "author": {
    "@type": "Organization",
    "name": "Privluxe"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Privluxe",
    "logo": {
      "@type": "ImageObject",
      "url": "https://privluxe.com/logo.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://privluxe.com/private-messaging-guide"
  }
};

export default function PrivateMessagingGuide() {
  const navigate = useNavigate();

  const handleCTAClick = (location) => {
    trackCTAClick(location, 'Private Messaging Guide');
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Complete Guide to Private Messaging - How to Communicate Securely | Privluxe</title>
        <meta name="description" content="Learn how to send private messages securely. This guide covers anonymous messaging, disappearing messages, and how to protect your identity online." />
        <meta name="keywords" content="private messaging guide, secure communication, anonymous messaging, disappearing messages, protect identity online, private chat guide" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://privluxe.com/private-messaging-guide" />
        <script type="application/ld+json">
          {JSON.stringify(seoSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#020617' }}>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(2, 6, 23, 0.9)', backdropFilter: 'blur(20px)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <a href="/" className="flex items-center gap-2">
                <Shield className="h-6 w-6" style={{ color: '#7C3AED' }} />
                <span className="text-lg font-bold tracking-[0.2em]" style={{ color: '#E5E7EB' }}>PRIVLUXE</span>
              </a>
              <Button 
                onClick={() => handleCTAClick('navbar_cta')}
                className="text-white text-sm px-6"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                Start Chatting
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-16 px-4">
          <div 
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }}
          />
          
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(124, 58, 237, 0.2)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
              <Lock className="h-4 w-4" style={{ color: '#7C3AED' }} />
              <span className="text-sm" style={{ color: '#E5E7EB' }}>Privacy Guide</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ color: '#E5E7EB' }}>
              Complete Guide to<br />
              <span style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Private Messaging
              </span>
            </h1>

            <p className="text-lg mb-6 max-w-3xl" style={{ color: 'rgba(229, 231, 235, 0.85)' }}>
              Everything you need to know about communicating privately online—without exposing your identity, phone number, or personal details.
            </p>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="pb-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#E5E7EB' }}>In This Guide</h2>
              <ul className="space-y-2">
                <li><a href="#what-is-private-messaging" className="hover:underline" style={{ color: '#22D3EE' }}>What is Private Messaging?</a></li>
                <li><a href="#why-privacy-matters" className="hover:underline" style={{ color: '#22D3EE' }}>Why Online Privacy Matters</a></li>
                <li><a href="#how-to-message-privately" className="hover:underline" style={{ color: '#22D3EE' }}>How to Message Privately</a></li>
                <li><a href="#features-for-privacy" className="hover:underline" style={{ color: '#22D3EE' }}>Key Features for Private Communication</a></li>
                <li><a href="#common-mistakes" className="hover:underline" style={{ color: '#22D3EE' }}>Common Privacy Mistakes to Avoid</a></li>
              </ul>
            </div>
          </div>
        </section>

        {/* What is Private Messaging */}
        <section id="what-is-private-messaging" className="py-12 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#E5E7EB' }}>
              What is Private Messaging?
            </h2>
            
            <p className="text-base mb-6" style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
              Private messaging means communicating in a way that protects your personal information from being exposed. This includes:
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
                <span style={{ color: 'rgba(229, 231, 235, 0.8)' }}><strong style={{ color: '#E5E7EB' }}>Identity protection</strong> — The person you're messaging doesn't know your real name, phone number, or email unless you choose to share it.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
                <span style={{ color: 'rgba(229, 231, 235, 0.8)' }}><strong style={{ color: '#E5E7EB' }}>Message control</strong> — You decide what happens to your messages. Can they be saved? Do they disappear? You're in control.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 shrink-0" style={{ color: '#22D3EE' }} />
                <span style={{ color: 'rgba(229, 231, 235, 0.8)' }}><strong style={{ color: '#E5E7EB' }}>Secure delivery</strong> — Messages are delivered in a way that prevents unauthorized access.</span>
              </li>
            </ul>

            <p className="text-base" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              True private messaging goes beyond just "encryption." It means the platform itself is designed to minimize what personal data is exposed and to whom.
            </p>
          </div>
        </section>

        {/* Why Privacy Matters */}
        <section id="why-privacy-matters" className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#E5E7EB' }}>
              Why Online Privacy Matters
            </h2>
            
            <p className="text-base mb-8" style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
              Every time you share your phone number or email with someone online, you're giving them permanent access to contact you—and potentially share that information with others.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,100,100,0.08)', border: '1px solid rgba(255,100,100,0.2)' }}>
                <AlertTriangle className="h-6 w-6 mb-3" style={{ color: '#FF6B6B' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Unwanted contact</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Once someone has your number, they can contact you forever—even if you want them to stop.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,100,100,0.08)', border: '1px solid rgba(255,100,100,0.2)' }}>
                <AlertTriangle className="h-6 w-6 mb-3" style={{ color: '#FF6B6B' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Data sharing</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Your contact info can be shared, sold, or leaked without your knowledge.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,100,100,0.08)', border: '1px solid rgba(255,100,100,0.2)' }}>
                <AlertTriangle className="h-6 w-6 mb-3" style={{ color: '#FF6B6B' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Identity linking</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Phone numbers can be used to find your social media, address, and more.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,100,100,0.08)', border: '1px solid rgba(255,100,100,0.2)' }}>
                <AlertTriangle className="h-6 w-6 mb-3" style={{ color: '#FF6B6B' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Harassment risk</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Giving out personal details to the wrong person can lead to harassment.</p>
              </div>
            </div>

            <p className="text-base" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Private messaging lets you communicate without these risks. You can always choose to share more later—but you can't take back information once it's out.
            </p>
          </div>
        </section>

        {/* How to Message Privately */}
        <section id="how-to-message-privately" className="py-12 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#E5E7EB' }}>
              How to Message Privately with Privluxe
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>1</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Create a Private Account</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Sign up with your email or phone. This is used only for account security and recovery—never shown to people you chat with.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>2</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Choose a Username</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Pick a username that doesn't reveal your identity. This is what other people will see—not your real name or contact info.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>3</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Send Anonymous Invites</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Generate invite links to share with anyone you want to chat with. The link connects to your account but doesn't reveal your personal details.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>4</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Chat with Full Control</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Once connected, use features like disappearing messages and view-once media to control what the other person can save.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section id="features-for-privacy" className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              Key Features for Private Communication
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                <UserX className="h-8 w-8 mb-4" style={{ color: '#7C3AED' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Anonymous Invites</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Start conversations without the other person knowing your phone number, email, or real name.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                <Clock className="h-8 w-8 mb-4" style={{ color: '#22D3EE' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Disappearing Messages</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Set messages to automatically delete after they're read. No trace left behind.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                <Eye className="h-8 w-8 mb-4" style={{ color: '#22D3EE' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>View-Once Media</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Send photos and videos that can only be viewed once before disappearing.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                <EyeOff className="h-8 w-8 mb-4" style={{ color: '#7C3AED' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Hidden Online Status</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>We don't tell others if you're online or when you last used the app.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section id="common-mistakes" className="py-12 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              Common Privacy Mistakes to Avoid
            </h2>

            <div className="space-y-4">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Using your real name as username</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Choose a username that doesn't reveal your identity. Your real name can be used to find you elsewhere online.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Sharing personal info early</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Wait until you trust someone before sharing your phone number, email, or other personal details. You can always share later, but you can't unsend information.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Not using disappearing messages</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>For sensitive conversations, enable disappearing messages. Even if you trust the person now, circumstances can change.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Sending regular photos of sensitive content</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Use view-once for any photos or videos you wouldn't want saved or shared. Regular attachments can be saved and forwarded.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 text-center" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#E5E7EB' }}>
              Start Messaging Privately Today
            </h2>
            <p className="text-lg mb-8" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Put this guide into practice. Create your private account and start having conversations without exposing your identity.
            </p>
            <div className="flex flex-col items-center gap-2">
              <Button 
                onClick={() => handleCTAClick('final_cta')}
                className="h-14 px-10 text-white text-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="guide-final-cta"
              >
                Start Private Chat
              </Button>
              <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
